import { api, router, state, render, siteFromPath, siteCan, pathForSite, openModal, closeModal } from './state.js';
import Shell from '/core/js/shell.js';
import modules from './modules/index.js';
import { esc } from '/core/js/utils.js';
import { icon } from './icons.js';
import { setDocTitle, titleOwned } from './components/ui/index.js';

// The platform's own pages — importing triggers customElements.define().
import './components/home.js';
import './components/users.js';
import './components/audit.js';
import './components/access.js';
import './components/settings.js';
import './components/alerts.js';
import './components/notifications.js';
import './components/me.js';
import './components/dev-ui.js';

/**
 * The ARC console. One shell for everyone who uses ARC; what a person may
 * open is decided per community by the API, and the menu only offers what
 * it would allow.
 *
 * Communities: a site-scoped page lives at #/s/<slug>/… . The toolbar's
 * switcher lists the communities this person may open (/sites); choosing
 * one goes to the same page in that community. A module's menu item with
 * `sitePerm` shows only when the person holds that permission in the
 * current community, and its address is rewritten for the community
 * (`s/:site/parcels` → `s/southharbor/parcels`).
 *
 * The chrome (CONSOLE-UI-PLAN U1): an icon per menu item, a collapsible
 * rail (remembered in localStorage.arc_rail), the community's name under
 * the Community heading, a searchable community picker, document titles,
 * and the keyboard: `/` focuses the page's search, `?` lists the shortcuts,
 * Esc closes whatever is on top (each surface handles its own).
 */
const NAV_ICONS = {
  home: 'home', communities: 'grid', parcels: 'map', arb: 'approve', builders: 'builder', palettes: 'palette',
  colors: 'swatch', vendors: 'tag', types: 'layers', config: 'settings', attributes: 'list', forms: 'table',
  users: 'users', audit: 'audit', alerts: 'bell', notifications: 'link', access: 'shield', settings: 'settings',
  communities: 'community',
};

class ArcShell extends Shell {
  registerRoutes(auth) {
    router.route('/home',     auth(() => 'arc-home'));
    router.route('/users',    auth(() => 'arc-users'));
    router.route('/audit',    auth(() => 'arc-audit'));
    router.route('/access',   auth(() => 'arc-access'));
    router.route('/settings', auth(() => 'arc-settings'));
    router.route('/alerts',   auth(() => 'arc-alerts'));
    router.route('/notifications', auth(() => 'arc-notifications'));
    router.route('/me',       auth(() => 'arc-me'));
    router.route('/dev/ui',   auth(() => 'arc-dev-ui'));     // the component gallery (Admin), not in the menu
  }

  async onUser(user) {
    // The menu's community addresses carry a :site placeholder until a community is known.
    for (const a of document.querySelectorAll('.nav-item[href*=":site"]')) a.dataset.routeTemplate = a.getAttribute('href').slice(1);
    try {
      const res = await api.sites.list();
      state.sites = res.items ?? [];
      state.defaultSiteId = res.default_site_id ?? null;
    } catch (err) {
      state.sites = [];
    }
    this.#renderSwitcher();
    this.#syncSite();
    if (!this.#bound) {
      this.#bound = true;
      window.addEventListener('hashchange', () => { this.#syncSite(); this.#syncTitle(); });
      document.getElementById('site-select').addEventListener('change', e => this.#choose(e.target.value));
      document.getElementById('site-picker').addEventListener('change', e => this.#choose(e.detail.value));
    }
    this.#syncTitle();
  }
  #bound = false;

  /** The community for the page we are on, else the last used, the default, or the first. */
  static currentSite() {
    const path = (location.hash.slice(1) || '').split('?')[0];
    let site = siteFromPath(path);
    if (!site) {
      let remembered = null;
      try { remembered = localStorage.getItem('arc_site'); } catch {}
      site = state.sites.find(s => s.slug === remembered)
          ?? state.sites.find(s => s.id === state.defaultSiteId)
          ?? state.sites[0] ?? null;
    }
    return site;
  }

  /** The switcher chose a community: the same page there, or its Parcels map. */
  #choose(value) {
    if (value === '*') { router.navigate('/communities'); return; }
    const site = state.sites.find(s => String(s.id) === String(value));
    if (!site || site === state.site) return;
    try { localStorage.setItem('arc_site', site.slug); } catch {}
    const path = (location.hash.slice(1) || '').split('?')[0];
    router.navigate(path.startsWith('/s/') ? pathForSite(path, site) : `/s/${encodeURIComponent(site.slug)}/parcels`);
  }

  #renderSwitcher() {
    const box = document.getElementById('site-switcher');
    const sel = document.getElementById('site-select');
    sel.innerHTML = state.sites.map(s => `<option value="${esc(s.id)}">${esc(s.name)}</option>`).join('');
    const admin = Shell.can(state.user, 'Admin');
    document.getElementById('site-picker').configure({
      options: [...state.sites.map(s => ({ value: s.id, label: s.name, icon: 'community' })), ...(admin ? [{ value: '*', label: 'All communities…', icon: 'grid' }] : [])],
      value: state.site?.id ?? null, placeholder: 'Community', allowClear: false, compact: true, searchable: state.sites.length > 6,
    });
    box.hidden = state.sites.length === 0;
  }

  /** The community changed (or the page did): the switcher, the menu's addresses and which items show. */
  #syncSite() {
    const site = ArcShell.currentSite();
    state.site = site;
    const sel = document.getElementById('site-select');
    if (site && sel.value !== String(site.id)) sel.value = String(site.id);
    const picker = document.getElementById('site-picker');
    const onOverview = (location.hash.slice(1) || '').split('?')[0] === '/communities';
    if (onOverview) picker.value = '*';
    else if (site && String(picker.value) !== String(site.id)) picker.value = site.id;
    if (site) { try { localStorage.setItem('arc_site', site.slug); } catch {} }
    for (const a of document.querySelectorAll('.nav-item[data-route-template]')) {
      const t = a.dataset.routeTemplate;
      a.setAttribute('href', `#${site ? t.replace(':site', encodeURIComponent(site.slug)) : t}`);
      a.dataset.route = site ? t.replace(':site', encodeURIComponent(site.slug)).replace(/^\//, '') : t;
    }
    for (const a of document.querySelectorAll('.nav-item[data-site-perm]')) {
      a.hidden = !site || !siteCan(site, a.dataset.sitePerm);
    }
    // The Community heading says which one.
    const head = document.querySelector('#sidebar .nav-area[data-area="communities"] .nav-section');
    if (head) {
      let cap = head.nextElementSibling?.classList.contains('nav-caption') ? head.nextElementSibling : null;
      if (!cap) { cap = document.createElement('div'); cap.className = 'nav-caption'; head.after(cap); }
      cap.textContent = site?.name ?? '';
      cap.title = site?.name ?? '';
      cap.hidden = !site;
    }
    // An area's tab opens its first visible page (the core keeps the last page opened there afterwards).
    for (const t of document.querySelectorAll('[data-area-tab]')) {
      const area = document.querySelector(`#sidebar .nav-area[data-area="${CSS.escape(t.dataset.areaTab)}"]`);
      const first = area?.querySelector('.nav-item:not([hidden])');
      const href = t.getAttribute('href') ?? '';
      if (first && (href.includes(':site') || href.startsWith('#/s/') || href === '#/')) t.setAttribute('href', first.getAttribute('href'));
    }
    window.dispatchEvent(new CustomEvent('site-changed', { detail: site }));
  }

  /** A title for every page from its menu item; a page with a header sets a better one. */
  #syncTitle() {
    requestAnimationFrame(() => {
      if (titleOwned()) return;
      const item = document.querySelector('.nav-item.active .nav-label, .nav-item.active');
      const label = item?.textContent.trim();
      if (label) setDocTitle([label, item.closest('.nav-area')?.dataset.area === 'communities' || item.dataset.routeTemplate ? state.site?.name : null]);
    });
  }

  start() {
    super.start();
    this.#decorateNav();
    this.#rail();
    this.#keyboard();
  }

  /** An icon per menu item, from its route; the label wrapped so the rail can hide it. */
  #decorateNav() {
    for (const a of document.querySelectorAll('#sidebar .nav-item')) {
      if (a.querySelector('.nav-icon')) continue;
      const route = (a.dataset.routeTemplate ?? a.dataset.route ?? a.getAttribute('href') ?? '').replace(/^#?\/?/, '');
      const last = route.split('/').filter(Boolean).pop() ?? '';
      const name = NAV_ICONS[last] ?? 'dot';
      const dot = a.querySelector('.nav-dot');
      const label = document.createElement('span');
      label.className = 'nav-label';
      for (const n of [...a.childNodes]) if (n.nodeType === Node.TEXT_NODE && n.textContent.trim()) { label.textContent = n.textContent.trim(); n.remove(); }
      const ic = document.createElement('span');
      ic.className = 'nav-icon';
      ic.innerHTML = icon(name);
      if (dot) dot.replaceWith(ic); else a.prepend(ic);
      a.appendChild(label);
      a.title = label.textContent;
    }
  }

  /** The sidebar as a 72 px rail of icons, remembered. */
  #rail() {
    const app = document.getElementById('app');
    const btn = document.querySelector('[data-rail-toggle]');
    if (!btn) return;
    const apply = (on) => {
      app.classList.toggle('rail', on);
      btn.innerHTML = `${icon(on ? 'panel-right' : 'panel-left', { size: 14 })}<span>${on ? 'Expand' : 'Collapse'}</span>`;
      btn.setAttribute('aria-label', on ? 'Expand the menu' : 'Collapse the menu');
      btn.title = btn.getAttribute('aria-label');
      try { localStorage.setItem('arc_rail', on ? '1' : '0'); } catch {}
      window.dispatchEvent(new Event('resize'));
    };
    let on = false;
    try { on = localStorage.getItem('arc_rail') === '1'; } catch {}
    apply(on);
    btn.addEventListener('click', () => apply(!app.classList.contains('rail')));
  }

  /** `/` focuses the page's search, `?` lists the shortcuts; Esc is each surface's own. */
  #keyboard() {
    document.addEventListener('keydown', e => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const typing = t && (t.closest('input, textarea, select, [contenteditable="true"]'));
      if (typing) return;
      if (e.key === '/') {
        const search = document.querySelector('#page-content [data-page-search], #inspector-dock [data-page-search], #page-content input[type="search"]');
        if (search) { e.preventDefault(); search.focus(); search.select?.(); }
      } else if (e.key === '?') {
        e.preventDefault();
        ArcShell.shortcuts();
      }
    });
  }

  static shortcuts() {
    const { container } = openModal(`
      <div class="modal-header"><span>Keyboard shortcuts</span><button class="modal-close" type="button" data-close aria-label="Close">&times;</button></div>
      <div class="modal-body">
        <dl class="shortcuts">
          <dt><span class="kbd">/</span></dt><dd>Focus the search on this page</dd>
          <dt><span class="kbd">Esc</span></dt><dd>Close the drawer, menu or dialog on top</dd>
          <dt><span class="kbd">?</span></dt><dd>This sheet</dd>
          <div class="shortcuts-group">Lists and pickers</div>
          <dt><span class="kbd">↑</span> <span class="kbd">↓</span></dt><dd>Move through rows and options</dd>
          <dt><span class="kbd">Enter</span></dt><dd>Open the row, pick the option</dd>
          <dt><span class="kbd">Shift</span> + click</dt><dd>Select a range of rows</dd>
          <div class="shortcuts-group">Maps</div>
          <dt><span class="kbd">Shift</span> + drag</dt><dd>Select the lots in a box (when the list is in selection mode)</dd>
        </dl>
      </div>`);
    container.querySelector('[data-close]').addEventListener('click', () => closeModal());
  }
}

new ArcShell({
  api, router, state, render, modules,
  login: { title: 'ARC', sub: 'Communities console', foot: '// secure session' },
}).start();
